import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {
    Typography,
    IconButton, Grid, Drawer, Container,
} from "@mui/material";
import ResizeObserver from "resize-observer-polyfill";
import "react-spring-bottom-sheet/dist/style.css";
import {makeStyles} from "@mui/styles";
import {Dismiss} from "~/ui/icons";
import {VelocityTracker} from "~/apps/core/ui/BottomSheet/VelocityTracker";
import {PortTheme} from "~/ui/theme";


export type NativeBottomSheetProps = {
    title?: any
    open: boolean
    onClose: () => void
    children?: any
    containerClassName?: string
    isCalendar?: boolean
}


const useStyles = makeStyles<PortTheme>(theme => ({
    backdrop: {
        zIndex: 1300,
    },
    sheet: {
        borderRadius: "14px 14px 0 0",
        backgroundColor: theme.palette.background.paper,
    },
    title: {
        color: "#000000",
        width: "100%",
        textAlign: "center",
        paddingLeft: '40px'
    },
    knob: {
        width: 46,
        height: 3,
        margin: "6px 0",
        borderRadius: 3,
        backgroundColor: "#DADDE2"
    },
}))


type Movement = {
    id: number
    startX: number
    startY: number
    x: number
    y: number
    timeStamp: number
}

export const NativeBottomSheet = (props: NativeBottomSheetProps) => {
    const {title, children, open, onClose, containerClassName, isCalendar} = props;
    const classes = useStyles(props);

    const [mount, setMount] = useState(false);
    const $children = useRef<HTMLDivElement>();
    const $content = useRef<HTMLDivElement>();
    const $sheet = useRef<HTMLDivElement>();
    const $open = useRef(open);
    $open.current = open;

    const velocityTracker = useMemo(() => new VelocityTracker(), []);
    const [sheetState, setSheetState] = useState({
        animated: false,
        position: 100,
        scrollable: false
    });
    const animating = useRef(false);
    const scrollable = useRef(false);
    const snapPoint = useRef(0);
    const snapPoints = useRef([0]);
    const sheetOffset = useRef(0);
    const currentTouch = useRef<Movement>(null);
    const dragging = useRef(false);

    const getSnapPoints = useCallback(({height, maxHeight}: {height: number, maxHeight: number}): number[] => {
        const middle = window.innerHeight / 3 * 2;

        if (maxHeight > middle && !isCalendar) {
            return [
                Math.min(middle, maxHeight),
                Math.min(maxHeight, window.innerHeight),
            ];
        }
        return [maxHeight];
    }, []);

    const refreshSnapPoints = useCallback(() => {
        const height = $sheet.current.getBoundingClientRect().height;
        const maxHeight = height - $content.current.getBoundingClientRect().height + $content.current.scrollHeight;
        snapPoints.current = [0].concat(getSnapPoints({maxHeight, height}));
    }, [getSnapPoints]);

    const setOffset = useCallback((offset: number) => {
        refreshSnapPoints();

        const maxHeight = snapPoints.current[snapPoints.current.length - 1];
        const minHeight = snapPoints.current[0];

        if (snapPoint.current + offset > maxHeight) {
            offset = maxHeight - snapPoint.current;
        } else if (snapPoint.current + offset < minHeight) {
            offset = snapPoint.current - minHeight;
        }

        sheetOffset.current = offset;

        setSheetState(value => ({
            ...value,
            animated: false,
            position: Math.floor(snapPoint.current + sheetOffset.current),
        }));
    }, [refreshSnapPoints, setSheetState]);

    const snapToPoint = useCallback((snapPointIndex?: number) => {
        if (snapPointIndex == null) {
            const velocity = velocityTracker.computeCurrentVelocity();
            const so = sheetOffset.current + snapPoint.current;

            let direction = 0;
            if (Math.abs(velocity.vy) >= 0.3) {
                direction = velocity.vy;
            } else { // скорость движения недостаточна, тогда выбираем по смещению
                direction = currentTouch.current.y - currentTouch.current.startY;
            }

            if (direction < 0) { // движение вверх
                snapPointIndex = snapPoints.current.length - 1;
                for (let i = snapPoints.current.length - 1; i >= 0; --i) {
                    if (so - snapPoints.current[i] < 0) {
                        snapPointIndex = i;
                    }
                }
            } else if (direction > 0) { // движение вниз
                snapPointIndex = 0;
                for (let i = 0; i < snapPoints.current.length; ++i) {
                    if (so - snapPoints.current[i] > 0) {
                        snapPointIndex = i;
                    }
                }
            } else {
                snapPointIndex = 0;
                for (let i = 1; i < snapPoints.current.length; ++i) {
                    const distance = Math.abs(so - snapPoints.current[i]);
                    const minDistance = Math.abs(so - snapPoints.current[snapPointIndex]);
                    if (distance < minDistance) {
                        snapPointIndex = i;
                    }
                }
            }
        }

        if (snapPointIndex == 0) {
            onClose && onClose();
        } else {
            snapPoint.current = snapPoints.current[snapPointIndex];
            sheetOffset.current = 0;
            scrollable.current = snapPointIndex == snapPoints.current.length - 1;
            setSheetState({
                animated: true,
                position: snapPoint.current,
                scrollable: scrollable.current
            });
        }
    }, [velocityTracker, setSheetState]);

    const resizeObserver = useMemo(() => new ResizeObserver(() => {
        if (!dragging.current && $open.current) {
            refreshSnapPoints();
            snapToPoint(1);
        }
    }), [refreshSnapPoints]);

    const handleTouchStart = useCallback((event: TouchEvent) => {
        if (currentTouch.current != null) {
            return;
        }
        currentTouch.current = {
            id: event.changedTouches[0].identifier,
            startX: event.changedTouches[0].screenX,
            startY: event.changedTouches[0].screenY,
            x: event.changedTouches[0].screenX,
            y: event.changedTouches[0].screenY,
            timeStamp: event.timeStamp,
        };
        velocityTracker.addMovement({
            x: currentTouch.current.x,
            y: currentTouch.current.y,
            timeStamp: currentTouch.current.timeStamp
        });
    }, [velocityTracker]);

    const handleTouchMove = useCallback((event: TouchEvent) => {
        const touch = [...event.changedTouches].find(x => x.identifier == currentTouch.current?.id);
        if (!touch) {
            return;
        }

        velocityTracker.addMovement({
            x: touch.screenX,
            y: touch.screenY,
            timeStamp: event.timeStamp
        });

        const distance = Math.sqrt(Math.pow(touch.screenY - currentTouch.current.y, 2) + Math.pow(touch.screenX - currentTouch.current.x, 2))
        if (distance < 3) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();

        currentTouch.current.x = touch.screenX;
        currentTouch.current.y = touch.screenY;
        currentTouch.current.timeStamp = event.timeStamp;

        if (!dragging.current) {
            if ($content.current.scrollTop != 0) { // прокрутка листа
                return;
            }
            const dy = currentTouch.current.y - currentTouch.current.startY;
            if ($content.current.scrollTop == 0 && dy < 0 && snapPoint.current == snapPoints.current[snapPoints.current.length - 1]) { // достигли верхней точки и начинаем прокрутку внутри
                return;
            }
            dragging.current = true;
        }

        setOffset(currentTouch.current.startY - currentTouch.current.y);
    }, [velocityTracker, setOffset]);

    const handleTouchEnd = useCallback((event: TouchEvent) => {
        const touch = [...event.changedTouches].find(x => x.identifier == currentTouch.current?.id);
        if (!touch) {
            return;
        }
        if (dragging.current) {
            event.preventDefault();
            event.stopPropagation();
            snapToPoint();
        }
        currentTouch.current = null;
        dragging.current = false;
        velocityTracker.clear();
    }, [snapToPoint, velocityTracker]);

    const handleMouseDown = useCallback((event: MouseEvent) => {
        if (currentTouch.current) {
            return;
        }
        currentTouch.current = {
            id: -1,
            startX: event.screenX,
            startY: event.screenY,
            x: event.screenX,
            y: event.screenY,
            timeStamp: event.timeStamp,
        };
        velocityTracker.addMovement({
            x: currentTouch.current.x,
            y: currentTouch.current.y,
            timeStamp: currentTouch.current.timeStamp
        });
    }, [velocityTracker]);

    const handleMouseMove = useCallback((event: MouseEvent) => {
        if (!currentTouch.current) {
            return;
        }
        currentTouch.current.x = event.screenX;
        currentTouch.current.y = event.screenY;
        currentTouch.current.timeStamp = event.timeStamp;

        velocityTracker.addMovement({
            x: currentTouch.current.x,
            y: currentTouch.current.y,
            timeStamp: currentTouch.current.timeStamp
        });

        if (!dragging.current) {
            dragging.current = true;
        }

        setOffset(currentTouch.current.startY - currentTouch.current.y);
    }, [velocityTracker, setOffset]);

    const handleMouseUp = useCallback((event: MouseEvent) => {
        if (!currentTouch.current) {
            return;
        }
        if (dragging.current) {
            snapToPoint();
        }
        currentTouch.current = null;
        dragging.current = false;
        velocityTracker.clear();
    }, [snapToPoint, velocityTracker]);

    const handleAnimationStart = useCallback(() => {
        animating.current = true;
    }, [])

    const handleAnimationEnd = useCallback(() => {
        animating.current = false;
    }, [])

    const handleAnimationCancel = useCallback(() => {
        animating.current = false;
    }, [])

    useEffect(() => {
        if (!mount) {
            return
        }
        if (open) {
            // при открытии переходим в первое состояние
            refreshSnapPoints();
            snapToPoint(1);
        }
        // let overscrollBehavior = document.body.style.overscrollBehavior;
        // document.body.style.overscrollBehavior = "none";
        const sheet = $sheet.current;
        const childrenContainer = $children.current;

        resizeObserver.observe(childrenContainer);

        sheet.addEventListener("animationstart", handleAnimationStart);
        sheet.addEventListener("animationend", handleAnimationEnd);
        sheet.addEventListener("animationcancel", handleAnimationCancel);

        sheet.addEventListener("touchstart", handleTouchStart);
        // A blocking listener prevents Firefox's navbar to auto-hide on scroll.
        // It only needs to prevent scrolling on the drawer's content when open.
        // When closed, the overlay prevents scrolling.
        document.addEventListener("touchmove", handleTouchMove, {passive: !open});
        document.addEventListener("touchend", handleTouchEnd);

        sheet.addEventListener("mousedown", handleMouseDown);
        document.addEventListener("mousemove", handleMouseMove);
        document.addEventListener("mouseup", handleMouseUp);
        return () => {
            resizeObserver.unobserve(childrenContainer);

            sheet.removeEventListener("animationstart", handleAnimationStart);
            sheet.removeEventListener("animationend", handleAnimationEnd);
            sheet.removeEventListener("animationcancel", handleAnimationCancel);

            sheet.removeEventListener("touchstart", handleTouchStart);
            document.removeEventListener("touchmove", handleTouchMove);
            document.removeEventListener("touchend", handleTouchEnd);

            sheet.removeEventListener("mousedown", handleMouseDown);
            document.removeEventListener("mousemove", handleMouseMove);
            document.removeEventListener("mouseup", handleMouseUp);
        }
    }, [mount, open,
        handleTouchStart, handleTouchMove, handleTouchEnd,
        handleMouseDown, handleMouseMove, handleMouseUp,
        handleAnimationStart, handleAnimationEnd, handleAnimationCancel,
        refreshSnapPoints, snapToPoint, resizeObserver
    ]);

    useEffect(() => {
        setMount(true);
    }, [setMount]);

    return useMemo(() => (
        <Drawer
            open={open}
            onClose={onClose}
            anchor="bottom"
            style={{zIndex: 1300}}
            ModalProps={{
                keepMounted: true,
            }}
            PaperProps={{
                style: {
                    top: window.innerHeight - sheetState.position,
                    transition: sheetState.animated ? "top 200ms ease 0s" : null,
                    userSelect: "none",
                    backgroundColor: "transparent",
                }
            }}
        >
            <Container className={[classes.sheet, containerClassName && containerClassName].join(" ")} ref={$sheet} disableGutters>
                <Grid container direction="column" wrap="nowrap">
                    <Grid item container alignItems="center" justifyContent="center">
                        <div className={classes.knob}/>
                    </Grid>
                    <Grid item container wrap="nowrap" alignItems="center" padding="6px" paddingRight={1}>
                        <Typography variant="searchText" className={classes.title}>
                            {title}
                        </Typography>
                        <IconButton onClick={onClose} color="primary">
                            <Dismiss/>
                        </IconButton>
                    </Grid>
                </Grid>
                <div ref={$content} style={{overflow: sheetState.scrollable ? "auto" : "hidden"}}>
                    <div ref={$children}>
                        {children}
                    </div>
                </div>
            </Container>
        </Drawer>
    ), [classes, sheetState.animated, sheetState.scrollable, sheetState.position, children]);
}
